import {
  formatDataBR,
  type BlocoHorario,
  type CargaHoraria,
  type CargaHorariaParaSalvar,
  type Colaborador,
  type Escala,
  type EscalaDaCarga,
  type EspelhoCarga,
  type EspelhoFeriado,
  type HorarioIntegracao,
  type ReflexoFeriado,
  type VinculoCarga,
} from "@/api/escala.service";
import { cpfOnzeDigitos } from "@/utils/cpfUtils";
import { TEXT_CONTRAST, ensureContrast } from "@/theme";

export type NomeBloco = "util" | "sabado" | "domingo" | "feriado";
// Os blocos que um tipo de escala pode exigir. O feriado nunca é exigido: em branco, o feriado
// trabalhado fica "?" até ser resolvido à mão.
export type BlocoExigivel = "sabado" | "domingo";
export type CampoBloco = "entrada" | "intervalo_inicio" | "intervalo_fim" | "saida";

export const BLOCOS: NomeBloco[] = ["util", "sabado", "domingo", "feriado"];
export const BLOCOS_EXIGIVEIS: BlocoExigivel[] = ["sabado", "domingo"];
export const CAMPOS_BLOCO: CampoBloco[] = ["entrada", "intervalo_inicio", "intervalo_fim", "saida"];

export const ROTULO_BLOCO: Record<NomeBloco, string> = {
  util: "Dia útil",
  sabado: "Sábado",
  domingo: "Domingo",
  feriado: "Feriado",
};

export const ROTULO_CAMPO: Record<CampoBloco, string> = {
  entrada: "Entrada",
  intervalo_inicio: "Início do intervalo",
  intervalo_fim: "Fim do intervalo",
  saida: "Saída",
};

// Mesmo limite de descricaoCargaMax (carga_horaria.go).
export const DESCRICAO_CARGA_MAX = 60;

// Mesmo formato de corJornadaRe (carga_horaria.go), que aceita qualquer caixa e grava em minúsculas.
const COR = /^#[0-9a-f]{6}$/i;

export const corJornadaValida = (cor?: string | null) => COR.test((cor ?? "").trim());

// Cor como vai para o servidor: vazia ("Padrão") sai do payload.
export const corParaEnvio = (cor?: string | null) => (cor ?? "").trim().toLowerCase() || undefined;

// Cor da hora na grade. Sem cor é "Padrão": a cor normal da fonte. Sem fundo (tela clara e
// impressão) sai como foi escolhida; com fundo, o tom é ajustado até ser lido nele.
export const corDaJornada = (cor?: string | null, fundo?: string) =>
  !cor ? undefined : fundo ? ensureContrast(cor, fundo, TEXT_CONTRAST) : cor;

// Campo do formulário: "descricao", "cor" ou "<bloco>.<campo>".
export interface ErroCarga {
  campo: string;
  mensagem: string;
  // Mensagem como o servidor devolve, com o nome do bloco na frente.
  completa: string;
}

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;
const minutos = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));

// Tempo trabalhado no bloco, sem o intervalo, como Jornada.Minutos (horario.go).
export function minutosDoBloco(b: BlocoHorario) {
  const intervalo = b.intervalo_inicio && b.intervalo_fim ? minutos(b.intervalo_fim) - minutos(b.intervalo_inicio) : 0;
  return minutos(b.saida) - minutos(b.entrada) - intervalo;
}

export function blocoVazio(b?: Partial<BlocoHorario> | null) {
  return !b || CAMPOS_BLOCO.every((c) => !(b[c] ?? "").trim());
}

// Mesma normalização de chaveDescricaoCarga (carga_horaria.go): minúscula, sem acento e com
// espaços colapsados.
export function chaveDescricao(s: string) {
  return s.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

// Mesmas regras e mensagens de validarBloco (carga_horaria.go); devolve o primeiro erro do bloco.
export function validarBloco(nome: NomeBloco, b: Partial<BlocoHorario> | null | undefined, obrigatorio: boolean): ErroCarga | null {
  const rotulo = ROTULO_BLOCO[nome];
  const erro = (campo: CampoBloco, mensagem: string): ErroCarga => ({
    campo: `${nome}.${campo}`,
    mensagem,
    completa: `${rotulo}: ${mensagem}`,
  });
  if (blocoVazio(b)) return obrigatorio ? erro("entrada", "informe a entrada e a saída") : null;

  const v = Object.fromEntries(CAMPOS_BLOCO.map((c) => [c, (b?.[c] ?? "").trim()])) as Record<CampoBloco, string>;
  for (const c of CAMPOS_BLOCO) {
    if (v[c] && !HORA.test(v[c])) {
      return erro(c, `hora de ${ROTULO_CAMPO[c].toLowerCase()} inválida ("${v[c]}"), use HH:MM`);
    }
  }
  if (!v.entrada) return erro("entrada", "informe a entrada");
  if (!v.saida) return erro("saida", "informe a saída");
  if (minutos(v.saida) <= minutos(v.entrada)) return erro("saida", "a saída deve ser depois da entrada");
  if (!!v.intervalo_inicio !== !!v.intervalo_fim) {
    return erro(
      v.intervalo_inicio ? "intervalo_fim" : "intervalo_inicio",
      "informe o início e o fim do intervalo, ou deixe os dois em branco",
    );
  }
  if (v.intervalo_inicio) {
    if (minutos(v.intervalo_inicio) <= minutos(v.entrada)) {
      return erro("intervalo_inicio", "o início do intervalo deve ser depois da entrada");
    }
    if (minutos(v.intervalo_fim) <= minutos(v.intervalo_inicio)) {
      return erro("intervalo_fim", "o fim do intervalo deve ser depois do início");
    }
    if (minutos(v.intervalo_fim) >= minutos(v.saida)) {
      return erro("intervalo_fim", "o fim do intervalo deve ser antes da saída");
    }
  }
  return null;
}

// Erros da carga inteira: no máximo um na descrição, um na cor e um por bloco, na ordem do formulário.
// existentes serve para a descrição repetida, que o servidor também recusa.
export function validarCargaHoraria(c: Partial<CargaHoraria>, existentes: CargaHoraria[]): ErroCarga[] {
  const erros: ErroCarga[] = [];
  const descricao = (c.descricao ?? "").split(/\s+/).filter(Boolean).join(" ");
  const erroDescricao = (mensagem: string) => erros.push({ campo: "descricao", mensagem, completa: mensagem });
  if (!descricao) {
    erroDescricao("descrição da carga horária é obrigatória");
  } else if ([...descricao].length > DESCRICAO_CARGA_MAX) {
    erroDescricao(`descrição deve ter no máximo ${DESCRICAO_CARGA_MAX} caracteres`);
  } else if (existentes.some((e) => e.id !== c.id && chaveDescricao(e.descricao) === chaveDescricao(descricao))) {
    erroDescricao(`já existe uma carga horária com a descrição "${descricao}"`);
  }
  const cor = (c.cor ?? "").trim();
  if (cor && !COR.test(cor)) {
    const mensagem = `cor da jornada inválida ("${cor}"), use #rrggbb`;
    erros.push({ campo: "cor", mensagem, completa: mensagem });
  }
  for (const nome of BLOCOS) {
    const erro = validarBloco(nome, c[nome], nome === "util");
    if (erro) erros.push(erro);
  }
  // Como em salvar_carga_horaria, que só confere isso depois do formato: o bloco em branco que
  // uma escala ativa que usa a carga exige.
  const escalas = c.id ? existentes.find((e) => e.id === c.id)?.escalas_ativas : undefined;
  for (const bloco of BLOCOS_EXIGIVEIS) {
    const nomes = blocoVazio(c[bloco]) ? escalasQueExigem(bloco, escalas) : [];
    if (nomes.length) {
      const mensagem = motivoBlocoExigido(bloco, nomes);
      erros.push({ campo: `${bloco}.entrada`, mensagem, completa: mensagem });
    }
  }
  return erros;
}

// Bloco como vai para o servidor: em branco vira null; intervalo vazio sai do payload.
export function blocoParaEnvio(b?: Partial<BlocoHorario> | null): BlocoHorario | null {
  if (blocoVazio(b)) return null;
  const limpo = (s?: string) => (s ?? "").trim() || undefined;
  return {
    entrada: (b?.entrada ?? "").trim(),
    intervalo_inicio: limpo(b?.intervalo_inicio),
    intervalo_fim: limpo(b?.intervalo_fim),
    saida: (b?.saida ?? "").trim(),
  };
}

// "08:00 às 12:00 e 13:40 às 18:00"; sem intervalo, "08:00 às 18:00".
export function formatarBloco(b?: BlocoHorario | null) {
  if (blocoVazio(b)) return "Sem horário";
  if (b!.intervalo_inicio && b!.intervalo_fim) {
    return `${b!.entrada} às ${b!.intervalo_inicio} e ${b!.intervalo_fim} às ${b!.saida}`;
  }
  return `${b!.entrada} às ${b!.saida}`;
}

// Mesma regra de motivoCargaEmUso (carga_horaria.go): carga referenciada não se exclui, se inativa.
export const podeExcluirCarga = (c: CargaHoraria) => c.vinculados === 0 && c.escalas === 0 && c.dias === 0;

// "A", "A e B", "A, B e C", como juntarComE (escala_carga.go).
export const juntarComE = (itens: string[]) =>
  itens.length <= 1 ? itens.join("") : `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}`;

const rotuloMinusculo = (b: NomeBloco) => ROTULO_BLOCO[b].toLowerCase();

// Mesma tabela de blocosExigidosPeloTipo (escala_carga.go): o motor escala a pessoa nos dias que
// o tipo manda, então o bloco só pode faltar onde o tipo nunca escala aquele dia. diaFolgaFixa é
// ISO (6 = sábado, 7 = domingo) e só vale em FOLGA_FIXA.
export function blocosExigidosPeloTipo(tipo: string, diaFolgaFixa?: number | null): Record<BlocoExigivel, boolean> {
  switch (tipo) {
    case "FOLGA_FIXA_SAB_DOM":
      return { sabado: false, domingo: false };
    case "FOLGA_FIXA_SAB_ALT":
      return { sabado: true, domingo: false };
    case "FOLGA_FIXA":
      return { sabado: diaFolgaFixa !== 6, domingo: diaFolgaFixa !== 7 };
  }
  return { sabado: true, domingo: true };
}

// Blocos que o tipo da escala exige e a carga não tem.
export function blocosQueFaltam(c: CargaHoraria, tipo: string, diaFolgaFixa?: number | null) {
  const exigidos = blocosExigidosPeloTipo(tipo, diaFolgaFixa);
  return BLOCOS_EXIGIVEIS.filter((b) => exigidos[b] && blocoVazio(c[b]));
}

// Mesma mensagem de motivoCargaIncompativel (escala_carga.go); null se a carga serve ao tipo. O
// tipo sugerido usa só os blocos que a carga tem.
export function motivoCargaIncompativel(c: CargaHoraria, tipo: string, diaFolgaFixa?: number | null) {
  const faltam = blocosQueFaltam(c, tipo, diaFolgaFixa);
  if (!faltam.length) return null;
  const sugestao = !blocoVazio(c.sabado)
    ? "FOLGA_FIXA com folga no domingo"
    : !blocoVazio(c.domingo)
      ? "FOLGA_FIXA com folga no sábado"
      : "FOLGA_FIXA_SAB_DOM";
  const dias = faltam.map((b) => (b === "sabado" ? "sábados" : "domingos"));
  return (
    `a carga horária "${c.descricao}" não tem horário de ${faltam.map(rotuloMinusculo).join(" nem de ")}, ` +
    `e a escala do tipo ${tipo} tem ${dias.join(" e ")} trabalhados; ` +
    `escolha uma carga com ${faltam.length > 1 ? "esses horários" : "esse horário"} ou, ` +
    `para quem não trabalha nesses dias, uma escala ${sugestao}`
  );
}

// Versão curta, para a opção desabilitada no seletor de cargas da escala.
export function motivoCurtoCargaIncompativel(c: CargaHoraria, tipo: string, diaFolgaFixa?: number | null) {
  const faltam = blocosQueFaltam(c, tipo, diaFolgaFixa);
  return faltam.length
    ? `Sem horário de ${faltam.map(rotuloMinusculo).join(" nem de ")}, que este tipo de escala exige`
    : undefined;
}

// Opções do seletor de cargas da escala, por nome: as ativas mais as que a escala salva já tinha,
// mesmo inativadas.
export const opcoesDeCargaDaEscala = (cargas: CargaHoraria[], atuais: string[] = []) =>
  cargas
    .filter((c) => c.ativa || atuais.includes(c.id!))
    .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"));

// Mesmas regras de validateEscala e validarCargasDaEscala (template.go, escala_carga.go): ao menos
// uma carga; cada uma do client e servindo ao tipo; "ativa" só para a que entra agora no conjunto.
export function validarCargasDaEscala(e: Partial<Escala>, cargas: CargaHoraria[], atuais: string[] = []) {
  if (!e.com_carga_horaria) return null;
  const ids = [...new Set(e.cargas_horarias_ids ?? [])];
  if (!ids.length) return "escolha ao menos uma carga horária para a escala com carga horária";
  for (const id of ids) {
    const carga = cargas.find((c) => c.id === id);
    if (!carga) return "carga horária não encontrada";
    if (!carga.ativa && !atuais.includes(id)) return `a carga horária "${carga.descricao}" está inativa`;
    const motivo = e.tipo ? motivoCargaIncompativel(carga, e.tipo, e.dia_folga_fixa) : null;
    if (motivo) return motivo;
  }
  return null;
}

// Escalas ativas que usam a carga e exigem o bloco, como "Caixa (6X1)".
export function escalasQueExigem(bloco: BlocoExigivel, escalas: EscalaDaCarga[] = []) {
  return escalas
    .filter((e) => blocosExigidosPeloTipo(e.tipo, e.dia_folga_fixa)[bloco])
    .map((e) => `${e.nome} (${e.tipo})`);
}

// Mesma mensagem de motivoBlocoExigido (escala_carga.go).
export const motivoBlocoExigido = (bloco: BlocoExigivel, escalas: string[]) =>
  `não é possível remover o horário de ${rotuloMinusculo(bloco)}: a carga é usada ` +
  `${escalas.length > 1 ? "nas escalas" : "na escala"} ${juntarComE(escalas)}`;

// Bloco da carga que vale no dia, como TipoDoDia (motor/horario.go): o feriado, mesmo com a loja
// aberta, usa o próprio bloco e vence o sábado e o domingo.
export function blocoDoDia(iso: string, feriados: Pick<EspelhoFeriado, "data">[] = []): NomeBloco {
  if (feriados.some((f) => f.data === iso)) return "feriado";
  const [ano, mes, dia] = iso.split("-").map(Number);
  const semana = new Date(ano!, mes! - 1, dia!).getDay();
  return semana === 0 ? "domingo" : semana === 6 ? "sabado" : "util";
}

// Mesma regra de motivoCargaDoDia (alterar_carga_dia.go): a carga precisa ser da escala, estar ativa
// e ter o bloco do dia; a que o dia já tem continua valendo mesmo inativada. undefined se serve.
export function motivoCargaDoDia(c: EspelhoCarga, bloco: NomeBloco, atualId?: string) {
  if (!c.da_escala) return `a carga horária "${c.descricao}" não é uma das cargas desta escala`;
  if (!c.ativa && c.id !== atualId) return `a carga horária "${c.descricao}" está inativa`;
  if (blocoVazio(c[bloco])) return `a carga horária "${c.descricao}" não tem horário de ${ROTULO_BLOCO[bloco].toLowerCase()}`;
  return undefined;
}

export function rotuloCarga(c: CargaHoraria) {
  return c.ativa ? c.descricao : `${c.descricao} (inativa)`;
}

// Bloco para comparar: intervalo vazio é o mesmo que ausente, e o bloco em branco é "".
const chaveBloco = (b?: Partial<BlocoHorario> | null) =>
  blocoVazio(b) ? "" : CAMPOS_BLOCO.map((c) => (b?.[c] ?? "").trim()).join("|");

// Mesma regra de cargaEquivalente (integracao_carga.go): a carga é igual ao horário do sistema externo em
// todo bloco que ele informa; os outros blocos da carga não contam.
export function cargaEquivalente(c: CargaHoraria, h: HorarioIntegracao) {
  return (
    chaveBloco(c.util) === chaveBloco(h.util) &&
    (blocoVazio(h.sabado) || chaveBloco(c.sabado) === chaveBloco(h.sabado)) &&
    (blocoVazio(h.domingo) || chaveBloco(c.domingo) === chaveBloco(h.domingo))
  );
}

const textoJornada = (b: BlocoHorario) =>
  b.intervalo_inicio && b.intervalo_fim
    ? `${b.entrada}-${b.intervalo_inicio}/${b.intervalo_fim}-${b.saida}`
    : `${b.entrada}-${b.saida}`;

// O horário como descricaoDoHorario (integracao_carga.go) o escreve: "Seg-Sex 08:00-12:00/13:00-17:48 · Sáb 08:00-12:00".
export function textoDoHorario(h: HorarioIntegracao) {
  let texto = blocoVazio(h.sabado)
    ? `Seg-Sex ${textoJornada(h.util)}`
    : chaveBloco(h.sabado) === chaveBloco(h.util)
      ? `Seg-Sáb ${textoJornada(h.util)}`
      : `Seg-Sex ${textoJornada(h.util)} · Sáb ${textoJornada(h.sabado!)}`;
  if (!blocoVazio(h.domingo)) texto += ` · Dom ${textoJornada(h.domingo!)}`;
  return texto;
}

// Opções do seletor de carga do colaborador: as ativas mais a que ele já tem, mesmo inativada. Com o horário
// do sistema externo, só as ativas equivalentes a ele.
export function opcoesDeCarga(cargas: CargaHoraria[], atualId?: string | null, horario?: HorarioIntegracao | null) {
  return cargas.filter((c) => c.id === atualId || (c.ativa && (!horario || cargaEquivalente(c, horario))));
}

// Mesma regra de motivoCargaDoColaborador (carga_horaria.go): a carga nova tem de existir e estar
// ativa; a que o colaborador já tem continua valendo mesmo inativada.
export function motivoCargaDoColaborador(novaId: string | null | undefined, atualId: string | null | undefined, cargas: CargaHoraria[]) {
  if (!novaId || novaId === atualId) return null;
  const carga = cargas.find((c) => c.id === novaId);
  if (!carga) return "carga horária não encontrada";
  if (!carga.ativa) return `a carga horária "${carga.descricao}" está inativa`;
  return null;
}

// Mesma regra e mensagem de motivoCargaTravada (integracao_trava.go): com o horário do sistema externo, só
// vale carga equivalente a ele. A que o colaborador já tem continua valendo.
export function motivoCargaTravada(
  colaborador: Pick<Colaborador, "carga_horaria_id" | "horario_integracao" | "travado_por">,
  novaId: string | null | undefined,
  cargas: CargaHoraria[],
) {
  const horario = colaborador.horario_integracao;
  if (!horario || (novaId || null) === (colaborador.carga_horaria_id || null)) return null;
  const carga = novaId ? cargas.find((c) => c.id === novaId) : undefined;
  if (carga && cargaEquivalente(carga, horario)) return null;
  return `a carga horária precisa ser equivalente ao horário da integração (${colaborador.travado_por}): ${textoDoHorario(horario)}`;
}

// Carga da planilha pela descrição, ignorando maiúscula, acento e espaços. Vazio = sem carga.
export function resolverCargaPorDescricao(texto: string | undefined, cargas: CargaHoraria[]): { id?: string; erro?: string } {
  const chave = chaveDescricao(texto ?? "");
  if (!chave) return {};
  const carga = cargas.find((c) => chaveDescricao(c.descricao) === chave);
  if (!carga) return { erro: `carga horária "${(texto ?? "").trim()}" não encontrada` };
  if (!carga.ativa) return { erro: `a carga horária "${carga.descricao}" está inativa` };
  return { id: carga.id };
}

// Aba "Cargas horárias" do modelo de planilha: as ativas, com os horários de cada bloco.
export function linhasAbaCargas(cargas: CargaHoraria[]): string[][] {
  return [
    ["Carga horária", ...BLOCOS.map((b) => ROTULO_BLOCO[b])],
    ...cargas.filter((c) => c.ativa).map((c) => [c.descricao, ...BLOCOS.map((b) => formatarBloco(c[b]))]),
  ];
}

export interface ErroLinha {
  linha: number;
  erro: string;
  // Na linha repetida, a primeira com o mesmo valor; o ImportadorPlanilha escreve "(já está na linha N)".
  linha_anterior?: number;
}

// Linhas da planilha "Vincular cargas horárias" (matrícula, CPF, carga) para o que
// escala.vincular_cargas_horarias espera. Faz as checagens de vincularCargasImportador
// (vincular_cargas.go) antes de ir ao servidor; linha é 1-based, como lá.
export function resolverVinculosDeCarga(
  linhas: Record<string, string>[],
  colaboradores: Colaborador[],
  cargas: CargaHoraria[],
): { vinculos: VinculoCarga[]; erros: ErroLinha[] } {
  const vinculos: VinculoCarga[] = [];
  const erros: ErroLinha[] = [];
  const primeiraLinha = new Map<string, number>();

  linhas.forEach((l, i) => {
    const linha = i + 1;
    const erro = (mensagem: string) => erros.push({ linha, erro: mensagem });
    const matricula = (l.matricula ?? "").trim();
    const cpf = cpfOnzeDigitos(l.cpf ?? "");
    if (!matricula && !cpf) return erro("informe a matrícula ou o CPF");

    const porMatricula = matricula ? colaboradores.find((c) => (c.matricula ?? "").trim() === matricula) : undefined;
    if (matricula && !porMatricula) return erro(`colaborador com matrícula "${matricula}" não encontrado`);
    const porCpf = cpf ? colaboradores.filter((c) => cpfOnzeDigitos(c.cpf ?? "") === cpf) : [];
    if (cpf && porCpf.length === 0) return erro(`colaborador com CPF "${l.cpf?.trim()}" não encontrado`);
    if (cpf && !porMatricula && porCpf.length > 1) return erro(`o CPF "${l.cpf?.trim()}" é de mais de um colaborador — use a matrícula`);
    if (porMatricula && cpf && !porCpf.some((c) => c.id === porMatricula.id)) {
      return erro(`matrícula "${matricula}" e CPF "${l.cpf?.trim()}" são de colaboradores diferentes`);
    }
    const colaborador = (porMatricula ?? porCpf[0])!;

    const primeira = primeiraLinha.get(colaborador.id!);
    if (primeira) return erros.push({ linha, erro: "colaborador repetido", linha_anterior: primeira });
    primeiraLinha.set(colaborador.id!, linha);

    const chave = chaveDescricao(l.carga_horaria ?? "");
    if (!chave) {
      const travada = motivoCargaTravada(colaborador, null, cargas);
      if (travada) return erro(travada);
      vinculos.push({ colaborador_id: colaborador.id! });
      return;
    }
    const carga = cargas.find((c) => chaveDescricao(c.descricao) === chave);
    if (!carga) return erro(`carga horária "${(l.carga_horaria ?? "").trim()}" não encontrada`);
    const motivo =
      motivoCargaDoColaborador(carga.id, colaborador.carga_horaria_id, cargas) ??
      motivoCargaTravada(colaborador, carga.id, cargas);
    if (motivo) return erro(motivo);
    vinculos.push({ colaborador_id: colaborador.id!, carga_horaria_id: carga.id });
  });

  return { vinculos, erros };
}

// Coluna de hora da planilha "Importar cargas horárias", como "sabado.intervalo_inicio".
export const chaveColunaHora = (bloco: NomeBloco, campo: CampoBloco) => `${bloco}.${campo}`;

// Linhas da planilha "Importar cargas horárias" para o que escala.importar_cargas_horarias espera. Faz as
// checagens de cargaHorariaImportador (carga_horaria_importar.go) antes de ir ao servidor: as do
// formulário, a descrição já cadastrada e a repetida na planilha. Um erro por linha; linha é 1-based.
export function resolverCargasParaImportar(
  linhas: Record<string, string>[],
  existentes: CargaHoraria[],
): { cargas: CargaHorariaParaSalvar[]; erros: ErroLinha[] } {
  const cargas: CargaHorariaParaSalvar[] = [];
  const erros: ErroLinha[] = [];
  const primeiraLinha = new Map<string, number>();

  linhas.forEach((l, i) => {
    const linha = i + 1;
    const bloco = (nome: NomeBloco) =>
      Object.fromEntries(CAMPOS_BLOCO.map((c) => [c, l[chaveColunaHora(nome, c)] ?? ""])) as Record<CampoBloco, string>;
    const carga = {
      descricao: (l.descricao ?? "").split(/\s+/).filter(Boolean).join(" "),
      cor: l.cor ?? "",
      util: bloco("util"),
      sabado: bloco("sabado"),
      domingo: bloco("domingo"),
      feriado: bloco("feriado"),
    };
    // A descrição entra no controle de repetidas mesmo com erro no resto da linha, como no servidor.
    const chave = chaveDescricao(carga.descricao);
    const primeira = primeiraLinha.get(chave);
    if (chave && primeira === undefined) primeiraLinha.set(chave, linha);

    const [erro] = validarCargaHoraria(carga, existentes);
    if (erro) return erros.push({ linha, erro: erro.completa });
    if (primeira !== undefined) {
      return erros.push({ linha, erro: `descrição "${carga.descricao}" repetida`, linha_anterior: primeira });
    }
    cargas.push({
      descricao: carga.descricao,
      cor: corParaEnvio(carga.cor),
      util: blocoParaEnvio(carga.util)!,
      sabado: blocoParaEnvio(carga.sabado),
      domingo: blocoParaEnvio(carga.domingo),
      feriado: blocoParaEnvio(carga.feriado),
    });
  });

  return { cargas, erros };
}

// Aviso depois de salvar algo que muda a carga padrão: os dias refeitos nas escalas em rascunho e as
// escalas validadas ou aprovadas que ficaram como estavam. Sem reflexo, é só a mensagem base.
export function mensagemDoReflexoDeCarga(base: string, reflexo: { dias_reaplicados?: number; escalas_nao_alteradas?: number }) {
  let texto = base;
  if (reflexo.dias_reaplicados) texto += ` ${reflexo.dias_reaplicados} dia(s) das escalas em rascunho foram atualizados.`;
  if (reflexo.escalas_nao_alteradas) {
    texto += ` ${reflexo.escalas_nao_alteradas} escala(s) já validada(s) ou aprovada(s) não foram alteradas.`;
  }
  return { texto, tipo: reflexo.escalas_nao_alteradas ? ("warning" as const) : ("success" as const) };
}

// Aviso depois de salvar (ou importar) feriados: o reflexo no horário dos rascunhos com carga, como o da
// carga, e os rascunhos com gente escalada num feriado que fecha a loja, que o reflexo não transforma em
// folga. Um feriado diz a data uma vez; a importação, que pode trazer vários, diz a data de cada escala.
export function mensagemDoReflexoDeFeriado(base: string, reflexo: ReflexoFeriado) {
  const aviso = mensagemDoReflexoDeCarga(base, reflexo);
  const fechado = reflexo.trabalho_em_feriado_fechado ?? [];
  if (!fechado.length) return aviso;
  const pessoas = (n: number) => `${n} ${n === 1 ? "colaborador" : "colaboradores"}`;
  const umaData = fechado.every((f) => f.data === fechado[0]!.data);
  if (!umaData) {
    const onde = juntarComE(fechado.map((f) => `${f.escala_nome} em ${formatDataBR(f.data)} (${pessoas(f.colaboradores)})`));
    return {
      texto: `${aviso.texto} Há gente escalada em feriado que fecha a loja nas escalas em rascunho ${onde}: corrija na grade ou gere a escala de novo.`,
      tipo: "warning" as const,
    };
  }
  const escalas = juntarComE(fechado.map((f) => `${f.escala_nome} (${pessoas(f.colaboradores)})`));
  const onde = fechado.length > 1 ? "nas escalas em rascunho" : "na escala em rascunho";
  return {
    texto:
      `${aviso.texto} A loja fecha em ${formatDataBR(fechado[0]!.data)}, mas há gente escalada nesse dia ${onde} ` +
      `${escalas}: corrija na grade ou gere a escala de novo.`,
    tipo: "warning" as const,
  };
}
